import React, { useContext, useEffect, useState, useRef } from 'react'
import { Button, Dropdown } from 'react-bootstrap'
import PropTypes from 'prop-types'
import dynamic from 'next/dynamic'
import { useLazyQuery, useMutation } from '@apollo/client'

import styles from './style.module.scss'
import { GET_BOOKMARK_DETAILS, UPDATE_NEWS_ARTICLE_VIEWS, UPDATE_FANTASY_ARTICLE_VIEWS } from '@graphql/article/article.query'
import ToastrContext from '@shared/components/toastr/ToastrContext'
import { ADD_BOOKMARK, REMOVE_BOOKMARK } from '@graphql/article/article.mutation'
import { DOMAIN, TOAST_TYPE } from '@shared-submodule/sm-constants'
import GlobalEventsContext from '@shared/components/global-events/GlobalEventsContext'
import useModal from '@shared/hooks/useModal'
import { getToken } from '@shared/libs/token'
import { BookmarkIcon, BookmarkedIcon, ShareIconAction } from '@shared/components/ctIcons'

const SocialShareList = dynamic(() => import('@shared/components/socialShare/shareList'))
const SocialAuthModal = dynamic(() => import('@shared/components/socialAuthModal'), { ssr: false })
export default function ArticleActions({ article, type, seoData }) {
  const previewImg = `${DOMAIN}images/oneturf-Facebook-Preview.svg`
  const { dispatch } = useContext(ToastrContext)
  const [isNativeShare, setIsNativeShare] = useState(false)
  const [isBookmark, setIsBookmark] = useState(false)
  const bookmarkId = useRef()
  const { isShowing, toggle } = useModal()
  const { stateGlobalEvents, dispatchGlobalEvents: editGlobalEvent } = useContext(GlobalEventsContext)
  const token = getToken()

  useEffect(() => {
    if (token && isShowing) {
      toggle()
    }
  }, [token])

  const [getBookmarkDetails] = useLazyQuery(GET_BOOKMARK_DETAILS, {
    fetchPolicy: 'network-only',
    onCompleted: data => {
      if (data && data.getBookmarks) {
        setIsBookmark(data?.getBookmarks?.bIsBookmarked)
        bookmarkId.current = data?.getBookmarks?._id
      }
    }
  })

  const [updateNewsArticleViews] = useMutation(UPDATE_NEWS_ARTICLE_VIEWS)
  const [updateFantasyArticleViews] = useMutation(UPDATE_FANTASY_ARTICLE_VIEWS)

  const [addBookmark] = useMutation(ADD_BOOKMARK, {
    onCompleted: data => {
      if (data && data.addUserBookmark) {
        bookmarkId.current = data.addUserBookmark?._id
        dispatch({
          type: 'SHOW_TOAST',
          payload: { message: data.addUserBookmark.sMessage, type: TOAST_TYPE.Success }
        })
        editGlobalEvent({
          type: 'CHANGE_PROFILE',
          payload: { profileData: { ...stateGlobalEvents.profileData, nBookmarkCount: stateGlobalEvents.profileData.nBookmarkCount + 1 } }
        })
      }
    }
  })

  const [removeBookmark] = useMutation(REMOVE_BOOKMARK, {
    onCompleted: data => {
      if (data && data.deleteBookmark) {
        dispatch({
          type: 'SHOW_TOAST',
          payload: { message: data.deleteBookmark.sMessage, type: TOAST_TYPE.Success }
        })
        editGlobalEvent({
          type: 'CHANGE_PROFILE',
          payload: { profileData: { ...stateGlobalEvents.profileData, nBookmarkCount: stateGlobalEvents.profileData.nBookmarkCount - 1 } }
        })
      }
    }
  })

  function handleBookmark() {
    const sToken = getToken()
    if (sToken && sToken !== 'undefined' && sToken !== 'null') {
      if (isBookmark) {
        removeBookmark({ variables: { input: { _id: bookmarkId.current } } })
        setIsBookmark(false)
      } else {
        addBookmark({ variables: { input: { eBookmarkType: article?.oSeo?.eType, iArticleId: article?._id } } })
        setIsBookmark(true)
      }
    } else {
      toggle()
    }
  }

  useEffect(() => {
    if (article?.oSeo?.eType === 'ar') {
      updateNewsArticleViews({ variables: { input: { _id: article?._id } } })
    } else {
      updateFantasyArticleViews({ variables: { input: { _id: article?._id } } })
    }
  }, [])

  useEffect(() => {
    const t = getToken()
    if (t && t !== 'undefined' && t !== 'null') {
      getBookmarkDetails({ variables: { input: { iId: article?._id, eType: article?.oSeo?.eType } } })
    }
  }, [article])

  useEffect(() => {
    if (navigator.share) {
      setIsNativeShare(true)
    } else {
      setIsNativeShare(false)
    }
  }, [])

  async function nativeShare() {
    const url = `${DOMAIN}${seoData?.sSlug}`
    const title = seoData?.sTitle || article?.sTitle
    const postImg = article?.oImg?.sUrl ? `${DOMAIN}${article?.oImg?.sUrl}` : previewImg
    try {
      await navigator.share({
        title,
        url,
        postImg
      })
      // alert('Thanks for Sharing!')
    } catch (err) {
      // alert(`Couldn't share ${err}`)
    }
  }

  return (
    <>
      <div className={`${styles.shareList} d-flex align-items-center`}>
        {isNativeShare ? (
          <Button
            variant="link"
            onClick={() => nativeShare()}
            className={`${styles.item} ${styles.share} d-flex justify-content-center align-items-center`}>
            <ShareIconAction stroke="currentColor" />
            <span className={styles.label}>{'Share'}</span>
          </Button>
        ) : (
          <Dropdown>
            <Dropdown.Toggle
              variant="link"
              title={'Share'}
              id="dropdown-basic-share"
              className={`${styles.item} ${styles.share} d-flex justify-content-center align-items-center ms-auto`}>
              <ShareIconAction stroke="currentColor" />
              <span className={styles.label}>{'Share'}</span>
            </Dropdown.Toggle>
            <Dropdown.Menu className={`${styles.socialShareMenu}`}>
              <SocialShareList seoData={seoData} />
            </Dropdown.Menu>
          </Dropdown>
        )}
        <Button
          title={'Bookmark'}
          variant="link"
          className={`${styles.item} ${styles.bookmark} ${
            isBookmark && styles.active
          } d-flex justify-content-center align-items-center me-0`}
          onClick={handleBookmark}>
          <span className="d-block">
            {!isBookmark ? <BookmarkIcon stroke="currentColor" /> : <BookmarkedIcon fill="currentColor" stroke="currentColor" />}
          </span>
          <span className={`${styles.label}`}>{'Save'}</span>
        </Button>
      </div>
      {isShowing && <SocialAuthModal show={isShowing} onHide={toggle} />}
    </>
  )
}

ArticleActions.propTypes = {
  article: PropTypes.object,
  type: PropTypes.string,
  seoData: PropTypes.object
}
